import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { decomposeColor } from '@material-ui/core/styles';
import pulseData from 'images/logo/loading/pulse.json';
import sweepData from 'images/logo/loading/sweep.json';
import writeOnData from 'images/logo/loading/writeOn.json';
import lottie from 'lottie-web/build/player/lottie_light';
import PropTypes from 'prop-types';

// Components
import { Mui } from 'components';

const VARIANTS = {
  writeOn: writeOnData,
  pulse: pulseData,
  sweep: sweepData,
};

const RATIO = 104 / 192;
const LEGIBLE_HEIGHT = 40;
const FINISH_MS = 450;
const RAMP_MS = 220;
const MAX_FINISH_SPEED = 4;

const tintCache = new Map();

const toLottieColor = color => {
  try {
    const [red, green, blue, alpha = 1] = decomposeColor(color).values;

    return [red / 255, green / 255, blue / 255, alpha];
  } catch (error) {
    return null;
  }
};

const tint = (node, rgba) => {
  if (Array.isArray(node)) return node.map(item => tint(item, rgba));
  if (node === null || typeof node !== 'object') return node;

  const out = {};
  Object.keys(node).forEach(key => {
    const value = node[key];
    const isColor = key === 'c' && value && Array.isArray(value.k) && value.k.length === 4;

    out[key] = isColor ? { ...value, k: rgba } : tint(value, rgba);
  });

  return out;
};

const rampSpeed = (animation, from, to) => {
  let raf = null;
  const startedAt = window.performance.now();
  const step = now => {
    const t = Math.min(1, (now - startedAt) / RAMP_MS);
    const eased = t * t * (3 - 2 * t);

    animation.setSpeed(from + (to - from) * eased);
    if (t < 1) raf = window.requestAnimationFrame(step);
  };
  raf = window.requestAnimationFrame(step);

  return () => {
    if (raf) window.cancelAnimationFrame(raf);
  };
};

const runToComplete = (animation, speed, onDone) => {
  const marker = ((animation.animationData || {}).markers || []).find(m => m.cm === 'complete');
  if (!marker) return null;

  const from = animation.firstFrame + animation.currentFrame;
  const target = marker.tm;

  if (from <= 0 || from >= target) return null;

  const rate = animation.frameRate || 60;
  const distance = target - from;
  const top = Math.min(MAX_FINISH_SPEED, Math.max(speed, distance / ((FINISH_MS / 1000) * rate)));

  animation.setLoop(false);

  let settled = false;
  let handoff = null;
  const settle = () => {
    if (settled) return;
    settled = true;
    animation.goToAndStop(target, true);
    handoff = window.requestAnimationFrame(() => onDone());
  };

  const onFrame = () => {
    if (animation.firstFrame + animation.currentFrame >= target) settle();
  };
  animation.addEventListener('enterFrame', onFrame);

  const stopRamp = rampSpeed(animation, speed, top);
  const bail = window.setTimeout(settle, (FINISH_MS + RAMP_MS) * 2);

  return alive => {
    window.clearTimeout(bail);
    if (handoff) window.cancelAnimationFrame(handoff);
    stopRamp();
    if (alive) animation.removeEventListener('enterFrame', onFrame);
  };
};

const useStyles = Mui.makeStyles({
  root: {
    display: 'inline-flex',
    flexShrink: 1,
    width: props => props.size,
    height: props => props.size * props.ratio,
    maxWidth: '100%',
    maxHeight: '100%',
  },
});

function SymbolLoading(props) {
  const { variant, color, size, loop, speed, finishing, onFinished } = props;

  // Hooks
  const theme = Mui.useTheme();
  const containerRef = useRef(null);
  const animationRef = useRef(null);
  const [measured, setMeasured] = useState(null);

  useLayoutEffect(() => {
    if (variant) return;
    const box = containerRef.current.getBoundingClientRect();
    const markHeight = Math.min(box.height, box.width * RATIO);

    setMeasured(markHeight >= LEGIBLE_HEIGHT ? 'writeOn' : 'pulse');
  }, [variant, size]);

  // Constants
  const active = variant || measured;
  const source = VARIANTS[active] || VARIANTS.writeOn;
  const resolvedColor =
    color ||
    (theme.palette.context?.current
      ? theme.palette.context.main
      : theme.palette.brand?.green?.[700]);

  const classes = useStyles({ size, ratio: RATIO });

  const serialized = useMemo(() => {
    if (!active) return null;
    const key = `${active}|${resolvedColor}`;
    if (!tintCache.has(key)) {
      const rgba = toLottieColor(resolvedColor);

      tintCache.set(key, JSON.stringify(rgba ? tint(source, rgba) : source));
    }

    return tintCache.get(key);
  }, [active, source, resolvedColor]);

  useEffect(() => {
    if (!serialized) return undefined;
    const animation = lottie.loadAnimation({
      container: containerRef.current,
      renderer: 'svg',
      loop,
      autoplay: true,
      animationData: JSON.parse(serialized),
    });

    animation.setSpeed(speed);
    animationRef.current = animation;

    return () => {
      animationRef.current = null;
      animation.destroy();
    };
  }, [serialized, loop, speed]);

  useEffect(() => {
    const animation = animationRef.current;
    if (!animation) return undefined;

    if (!finishing) {
      animation.setSpeed(speed);
      animation.setLoop(loop);
      if (animation.isPaused) animation.play();

      return undefined;
    }

    const teardown = runToComplete(animation, speed, onFinished);
    if (!teardown) {
      onFinished();

      return undefined;
    }

    return () => teardown(animationRef.current === animation);
  }, [finishing, loop, speed, onFinished, serialized]);

  return <div className={classes.root} ref={containerRef} />;
}

SymbolLoading.propTypes = {
  variant: PropTypes.oneOf(['writeOn', 'pulse', 'sweep']),
  color: PropTypes.string,
  size: PropTypes.number,
  loop: PropTypes.bool,
  speed: PropTypes.number,
  finishing: PropTypes.bool,
  onFinished: PropTypes.func,
};

SymbolLoading.defaultProps = {
  variant: null,
  color: null,
  size: 96,
  loop: true,
  speed: 1,
  finishing: false,
  onFinished: () => {},
};

export default SymbolLoading;
